import { MaskedLine, FadeUp, ParallaxImage } from "@/components/motion";
import FinishMarquee from "@/components/FinishMarquee";

const CHAPTERS = [
  {
    n: "01",
    t: "Fresh",
    d: "SanCub represents the next era of bathware. We started with a simple observation: the bathroom is the first and last room you use every day, yet it is the room most often compromised. We refuse to compromise it.",
  },
  {
    n: "02",
    t: "Innovative",
    d: "Rimless flushes, demister glass, WELS-rated cartridges, solid-surface stone. Innovation for us is not novelty — it is the quiet engineering that makes a fitting disappear into daily life and keep working for decades.",
  },
  {
    n: "03",
    t: "Refined",
    d: "Clean design, flowing elegance, modern comfort. Every radius, knurl and finish is considered against a single question: does it earn its place in the room? If it doesn't, it doesn't ship.",
  },
  {
    n: "04",
    t: "Australian",
    d: "Inspired by water and crafted for living — designed in Melbourne for Australian homes, Australian water pressure and Australian light. Built to keep up with your family while keeping design at heart.",
  },
];

const About = () => (
  <main data-testid="about-page">
    <section className="mx-auto max-w-[1500px] px-6 pb-20 pt-32 md:px-10 md:pt-44">
      <FadeUp>
        <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50">
          The SanCub manifesto
        </p>
      </FadeUp>
      <h1 className="mt-6 max-w-5xl font-display text-5xl font-medium leading-[1.05] tracking-tight text-ink md:text-7xl lg:text-8xl" data-testid="about-headline">
        <MaskedLine delay={0.15}>The next era</MaskedLine>
        <MaskedLine delay={0.27}>of bathware.</MaskedLine>
      </h1>
      <FadeUp delay={0.55} className="mt-8 max-w-xl">
        <p className="text-base leading-relaxed text-ink/70 md:text-lg">
          Our identity reflects clean design, flowing elegance and modern comfort — inspired
          by water, crafted for living.
        </p>
      </FadeUp>
    </section>

    <FinishMarquee />

    <section className="mx-auto max-w-[1500px] px-6 py-24 md:px-10 md:py-36" data-testid="manifesto-chapters">
      {CHAPTERS.map((c, i) => (
        <div
          key={c.n}
          className="grid grid-cols-1 gap-8 py-14 md:grid-cols-12 md:py-20"
          data-testid={`chapter-${c.n}`}
        >
          <div className="md:col-span-4">
            <div className="md:sticky md:top-32">
              <FadeUp>
                <p className="font-display text-6xl font-medium leading-none text-ink/15 md:text-8xl">{c.n}</p>
                <p className="mt-4 font-display text-2xl font-medium tracking-tight text-ink md:text-3xl">
                  {c.t}
                </p>
              </FadeUp>
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-6">
            <FadeUp delay={0.15}>
              <p className="text-lg leading-relaxed text-ink/70 md:text-xl">{c.d}</p>
            </FadeUp>
            {i === 1 && (
              <FadeUp delay={0.2} className="mt-12">
                <ParallaxImage
                  src="https://images.pexels.com/photos/4194987/pexels-photo-4194987.jpeg?auto=compress&cs=tinysrgb&w=1260&h=800&dpr=1"
                  alt="Brass SanCub tapware detail"
                  className="h-[300px] rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:h-[420px]"
                  speed={0.1}
                />
              </FadeUp>
            )}
          </div>
        </div>
      ))}
    </section>

    <section className="mx-auto max-w-[1500px] px-6 pb-28 md:px-10 md:pb-40" data-testid="principles-section">
      <div className="rounded-[3rem] bg-ink p-10 text-white md:p-20">
        <FadeUp>
          <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-white/50">How we show up</p>
        </FadeUp>
        <h2 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-[1.1] tracking-tight md:text-6xl">
          <MaskedLine inView>Sophistication, with a quiet</MaskedLine>
          <MaskedLine inView delay={0.12}>sense of innovation.</MaskedLine>
        </h2>
        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { t: "Understated luxury", d: "Nothing shouts. Everything is felt — in the weight of a mixer, the warmth of stone." },
            { t: "Modern refinement", d: "Contemporary without being cold. Bathrooms built for real mornings and slow evenings." },
            { t: "Confidence in simplicity", d: "Fewer lines, better lines. The discipline to stop designing when the object is done." },
          ].map((v, i) => (
            <FadeUp key={v.t} delay={i * 0.1}>
              <div className="rounded-3xl bg-white/5 p-9 transition-colors duration-500 hover:bg-white/10" data-testid={`principle-${i + 1}`}>
                <p className="font-display text-xl font-medium tracking-tight">{v.t}</p>
                <p className="mt-4 text-sm leading-relaxed text-white/60">{v.d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  </main>
);

export default About;
